import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Heart, Landmark, Home, Key, Truck, Activity, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import SpotlightCard from '../ui/SpotlightCard';
import AnimatedBadge from '../ui/AnimatedBadge';
import { useContact } from '../../context/ContactContext';

const SLIDES = [
  {
    image: "/images/seguros/vida.png",
    title: "Seguro de Vida",
    phrase: "Proteção para quem você mais ama.",
    icon: <Heart className="text-white" />
  },
  {
    image: "/images/seguros/residencial.png",
    title: "Residencial",
    phrase: "Sua casa segura em todos os momentos.",
    icon: <Home className="text-white" />
  },
  {
    image: "/images/seguros/auto.png",
    title: "Frotas / Auto",
    phrase: "Segurança completa para seus veículos.",
    icon: <Truck className="text-white" />
  },
  {
    image: "/images/seguros/saude.png",
    title: "Planos de Saúde",
    phrase: "Cuidado e saúde para sua família.",
    icon: <Activity className="text-white" />
  },
  {
    image: "/images/seguros/consorcio.png",
    title: "Consórcios",
    phrase: "Conquiste seus sonhos com planejamento.",
    icon: <Landmark className="text-white" />
  }
];

export default function SegurosSection() {
  const { openModal } = useContact();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);

  const seguros = [
    { icon: Heart, title: "Seguro de Vida" },
    { icon: Landmark, title: "Consórcios" },
    { icon: Home, title: "Residencial" },
    { icon: Key, title: "Aluguel" },
    { icon: Truck, title: "Frotas / Auto" },
    { icon: Activity, title: "Planos de Saúde" }
  ];

  return (
    <section 
      ref={containerRef}
      id="seguros" 
      className="w-full bg-background py-48 md:py-64 relative overflow-hidden border-t border-foreground/5"
    >
      {/* Parallax Background */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 w-full h-[140%] pointer-events-none opacity-5"
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
          
          {/* Slider Left */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="relative z-10 rounded-[4rem] overflow-hidden border border-foreground/10 aspect-[4/5] shadow-2xl group">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, scale: 1.1, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
                  transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0"
                >
                  <img 
                    src={SLIDES[currentIndex].image} 
                    alt={SLIDES[currentIndex].title} 
                    className="w-full h-full object-cover grayscale-[10%] group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"></div>
                </motion.div>
              </AnimatePresence>

              {/* Slide Content Overlay */}
              <div className="absolute bottom-10 left-10 right-10 z-20">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-foreground/5 backdrop-blur-2xl border border-foreground/10 p-8 rounded-3xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-orange-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-orange-600/20">
                        {SLIDES[currentIndex].icon}
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-xl">{SLIDES[currentIndex].title}</h4>
                        <p className="text-slate-100/60 text-xs font-medium italic">"{SLIDES[currentIndex].phrase}"</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Slider Controls */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button 
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full bg-background/20 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-orange-600 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full bg-background/20 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-orange-600 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              {/* Indicators */}
              <div className="absolute top-8 right-8 flex gap-2 z-30">
                {SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      currentIndex === idx ? 'w-8 bg-orange-500' : 'w-2 bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Decorative element */}
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-orange-600/5 rounded-full blur-3xl -z-10"></div>
          </motion.div>

          <div className="lg:col-span-7 text-center lg:text-left px-4 md:px-0">
            <div className="mb-4 md:mb-6 flex justify-center lg:justify-start">
              <AnimatedBadge>
                Proteção Patrimonial
              </AnimatedBadge>
            </div>
            <h2 className="text-2xl md:text-5xl font-black text-foreground tracking-tight mb-6 md:mb-8 leading-tight">
              Segurança para o <br className="hidden md:block" /> que é essencial.
            </h2>
            <p className="text-sm md:text-xl text-slate-400 font-medium mb-8 md:mb-12 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Nossa corretora integrada oferece soluções de alta performance para blindar seu patrimônio e garantir tranquilidade.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
              {seguros.map((item, idx) => (
                <SpotlightCard key={idx} innerClassName="p-6 md:p-8 flex flex-col items-center text-center" radius="1.5rem">
                  <item.icon className="w-6 h-6 md:w-8 md:h-8 text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
                  <span className="text-[8px] md:text-[10px] font-black text-foreground uppercase tracking-widest leading-tight">{item.title}</span>
                </SpotlightCard>
              ))}
            </div>

            <div className="flex justify-center lg:justify-start px-6 md:px-0">
              <a 
                href="https://wa.me/5511949830659"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-[240px] px-10 py-5 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-full transition-all duration-300 flex items-center justify-center gap-3 uppercase tracking-widest text-[10px] shadow-xl shadow-orange-600/20 whitespace-nowrap"
              >
                Cotação Especializada
                <ArrowRight size={18} />
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

